import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import type { FilterControl } from '../../../../../types/filterControl';
import type { FilterSelectOption } from '../../../../../types/table';
import { changeSelection, type SelectFilterControl } from './helpers';

/** One value a control has set, removable on its own. */
type AppliedFilterValue = {
  key: string;
  label: string;
  isExcluded: boolean;
  /** Clears this value in one write; the control's other values stay. */
  remove: () => void;
};

/**
 * Every value a control has set, each include and exclude its own entry. Matches `getControlActiveCount`: one entry
 * per counted value, so the applied view lists exactly what the badges count.
 */
export const getAppliedFilterValues = (
  control: FilterControl,
  texts: { yes: string; no: string }
): AppliedFilterValue[] => {
  switch (control.type) {
    case 'multi-select':
      return [
        ...control.value.include.map((value) => selectValue(control, value, false)),
        ...control.value.exclude.map((value) => selectValue(control, value, true))
      ];
    case 'single-select': {
      const { include, exclude } = control.value;
      return [
        ...(include === null ? [] : [selectValue(control, include, false)]),
        ...exclude.map((value) => selectValue(control, value, true))
      ];
    }
    case 'date-range':
      return rangeValues(
        control.value,
        ({ from, to }) => boundsLabel(formatDate(from), formatDate(to)),
        (next) => control.onChange(next)
      );
    case 'number-range':
      return rangeValues(
        control.value,
        ({ min, max }) => boundsLabel(min, max),
        (next) => control.onChange(next)
      );
    case 'time-range':
      return control.value && (control.value.from || control.value.to)
        ? [single(boundsLabel(control.value.from, control.value.to), () => control.onChange(undefined))]
        : [];
    case 'boolean':
      return control.value === null ? [] : [single(control.value ? texts.yes : texts.no, () => control.onChange(null))];
    case 'checkbox':
      return control.value ? [single(texts.yes, () => control.onChange(false))] : [];
    case 'date':
      return control.value
        ? [single(noddiFormatDate(control.value, NoddiDateFormats.DATE), () => control.onChange(undefined))]
        : [];
    case 'number-input':
      return control.value === null ? [] : [single(String(control.value), () => control.onChange(null))];
    case 'text':
      return control.value ? [single(control.value, () => control.onChange(null))] : [];
  }
};

const selectValue = (control: SelectFilterControl, value: string | number, isExcluded: boolean) => ({
  key: `${isExcluded ? 'exclude' : 'include'}-${value}`,
  label: optionLabel(control.options, value),
  isExcluded,
  remove: () => changeSelection(control, value, 'clear')
});

// Options load with the sidebar; until they do, the raw value is the only name there is.
const optionLabel = (options: FilterSelectOption[], value: string | number) =>
  options.find((option) => option.value === value)?.label ?? String(value);

const single = (label: string, remove: () => void): AppliedFilterValue => ({
  key: 'value',
  label,
  isExcluded: false,
  remove
});

const rangeValues = <TRange extends object>(
  value: { include: TRange | undefined; exclude: TRange | undefined },
  label: (range: TRange) => string,
  onChange: (next: { include: TRange | undefined; exclude: TRange | undefined }) => void
): AppliedFilterValue[] => {
  const entries: AppliedFilterValue[] = [];
  if (value.include && hasBound(value.include)) {
    entries.push({
      key: 'include',
      label: label(value.include),
      isExcluded: false,
      remove: () => onChange({ ...value, include: undefined })
    });
  }
  if (value.exclude && hasBound(value.exclude)) {
    entries.push({
      key: 'exclude',
      label: label(value.exclude),
      isExcluded: true,
      remove: () => onChange({ ...value, exclude: undefined })
    });
  }
  return entries;
};

const hasBound = (range: object) => Object.values(range).some((bound) => bound !== undefined && bound !== null);

// The sidebar's range picker shows this short format; a range of long dates would not fit the sidebar's width.
const formatDate = (date: Date | undefined) =>
  date ? noddiFormatDate(date, CustomDateFormats.DATE_SHORT_YEAR_NUMBERS_ONLY) : undefined;

// An open end reads as "…", so "5 – …" is "5 and up".
const boundsLabel = (from: string | number | undefined, to: string | number | undefined) =>
  `${from ?? '…'} – ${to ?? '…'}`;
